import type { SupportedCountryCode } from '@seek/ca-myprofile-utils';
import { mapDropdownItemToOption } from '@seek/profile-edit-ui';
import { valueBetween } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import {
  Column,
  Columns,
  Dropdown,
  Notice,
  Stack,
  Text,
  TextField,
} from 'braid-design-system';
import React, { useState } from 'react';

import { useAppConfig } from 'src/modules/appConfig';
import type { ActionName } from 'src/types';
import type * as Schema from 'src/types/graphql';

import { type SalaryFormValues, useSalaryFormField } from '../EditSalary';

import translations from './.vocab';
import { useSalaryRange } from './salaryRange';
import { formatSalary, getNewSalaryValues, useSalaryOptions } from './utils';

export type SalaryCurrency =
  | 'AUD'
  | 'NZD'
  | 'HKD'
  | 'THB'
  | 'MYR'
  | 'SGD'
  | 'PHP'
  | 'IDR'
  | 'GBP'
  | 'USD';

interface SalaryItemProps {
  label: string;
  countryCode: SupportedCountryCode;
  currencyCode: string;
  isFieldDirty: Record<keyof SalaryFormValues, boolean>;
  setFieldValue: <FieldId extends keyof SalaryFormValues>(
    fieldId: FieldId,
    value: SalaryFormValues[FieldId],
  ) => void;
  addSalaryEvent: ({
    actionName,
    salaryCurrency,
  }: {
    actionName: ActionName;
    salaryCurrency: string;
  }) => void;
}

export const SalaryItem = React.forwardRef(
  (
    {
      label,
      countryCode,
      currencyCode,
      isFieldDirty,
      setFieldValue,
      addSalaryEvent,
    }: SalaryItemProps,
    ref: React.Ref<HTMLInputElement>,
  ) => {
    const { t } = useTranslations(translations);
    const { locale, language: languageCode, site } = useAppConfig();

    // localeIntl used by the `Intl` object to return the currency value
    // based on the language and country of the site the candidate visited
    const localeIntl = `${languageCode}-${locale}`;

    const salaryTypeField = useSalaryFormField({
      id: `salaryType_${countryCode}`,
    });

    const salaryRange = useSalaryRange(
      countryCode,
      salaryTypeField.value as Schema.SalaryType2,
      languageCode,
      currencyCode as SalaryCurrency,
    );

    const {
      value: amountFieldValue,
      onChange: onAmountFieldChange,
      onBlur: onAmountFieldBlur,
      ...salaryAmountField
    } = useSalaryFormField({
      id: `amount_${countryCode}`,
      validators: [valueBetween],
      validatorProps: salaryRange,
    });

    const [formattedSalaryAmount, setFormattedSalaryAmount] = useState(
      amountFieldValue
        ? formatSalary({
            amount: amountFieldValue,
            currencyCode,
            localeIntl,
          })
        : '',
    );

    const [initialSalaryAmount, setInitialSalaryAmount] =
      useState(amountFieldValue);

    const salaryOptions = useSalaryOptions(site);

    const actionName = !initialSalaryAmount ? 'ADD' : 'EDIT';

    const showSalaryAlert = !Boolean(amountFieldValue);
    const reserveMessageSpace = !showSalaryAlert;

    const isWithinSalaryRange =
      !Boolean(amountFieldValue) ||
      (amountFieldValue &&
        salaryRange &&
        amountFieldValue >= salaryRange.min &&
        amountFieldValue <= salaryRange.max);

    const onSalaryTypeChange = (event: React.FormEvent<HTMLSelectElement>) => {
      const newSalaryType = event.currentTarget.value as Schema.SalaryType2;
      if (newSalaryType !== salaryTypeField.value) {
        setFieldValue(`amount_${countryCode}`, null);
        setFormattedSalaryAmount('');
        salaryTypeField.onChange(newSalaryType);
      }
    };

    const onAmountChange = (event: React.FormEvent<HTMLInputElement>) => {
      const newAmountFieldValue = event.currentTarget.value;
      const { strippedSalaryAmount, formattedNewSalaryFieldValue } =
        getNewSalaryValues(newAmountFieldValue, localeIntl, currencyCode);

      onAmountFieldChange(strippedSalaryAmount);
      setFormattedSalaryAmount(formattedNewSalaryFieldValue);
    };

    const updateSalary = () => {
      onAmountFieldBlur();

      // Only add to tracking list if value is dirty and valid
      if (isFieldDirty[`amount_${countryCode}`] && isWithinSalaryRange) {
        addSalaryEvent({
          actionName,
          salaryCurrency: currencyCode,
        });

        setInitialSalaryAmount(amountFieldValue);
      }
    };

    return (
      <Stack space="small">
        <Columns space="small" collapseBelow="tablet" alignY="bottom">
          <Column>
            <TextField
              id={`salary-field-${countryCode}`}
              data={{ automation: `salary-field-${countryCode}` }}
              value={formattedSalaryAmount}
              placeholder={t('Enter amount')}
              label={label}
              prefix={currencyCode}
              reserveMessageSpace={reserveMessageSpace}
              ref={ref}
              {...salaryAmountField}
              onChange={onAmountChange}
              onBlur={updateSalary}
            />
          </Column>
          <Column width="content">
            <Dropdown
              id={`salary-type-dropdown-${countryCode}`}
              data={{ testid: `salary-type-dropdown-${countryCode}` }}
              placeholder={t('Select type...')}
              reserveMessageSpace={reserveMessageSpace}
              {...salaryTypeField}
              value={salaryTypeField.value as Schema.SalaryType2}
              onChange={onSalaryTypeChange}
              aria-label={label}
            >
              {salaryOptions.map(mapDropdownItemToOption)}
            </Dropdown>
          </Column>
        </Columns>
        {showSalaryAlert && (
          <Notice tone="info">
            <Text>{t('You will not appear in salary searches...')}</Text>
          </Notice>
        )}
      </Stack>
    );
  },
);
